import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PackageIcon, FlowerIcon, ArrowLeftIcon, ClockIcon,
  CurrencyDollarIcon, BoxArrowDownIcon, WarehouseIcon, PaperclipIcon
} from "@phosphor-icons/react";
import DocumentCenter from "@/components/DocumentCenter";
import { useAuth } from "@/contexts/AuthContext";
import { usePrefFechaFacturacion } from "@/pages/_inventory/ExistenciaDetalle";
import DocumentoPdfModal from "@/components/DocumentoPdfModal";

const TABS = [
  { id: "info", label: "Información", icon: PackageIcon },
  { id: "peps", label: "Kardex PEPS (lotes)", icon: BoxArrowDownIcon },
  { id: "bodegas", label: "Stock por bodega", icon: WarehouseIcon },
  { id: "movs", label: "Movimientos", icon: ClockIcon },
  { id: "precios", label: "Precios", icon: CurrencyDollarIcon },
  { id: "documentos", label: "Documentos", icon: PaperclipIcon },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prod, setProd] = useState(null);
  const [tab, setTab] = useState("info");
  const { user } = useAuth();
  // Roles sin acceso a costos: el servidor ya no los envía; la pantalla no muestra sus casillas
  const sinCostos = Boolean(user?.effective?.filtro_informacion_restringida);
  const [entries, setEntries] = useState([]);
  const [consumptions, setConsumptions] = useState([]);
  const [movs, setMovs] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [byWarehouse, setByWarehouse] = useState([]);

  const load = async () => {
    try {
      const p = await api.get(`/products/${id}`).then((r) => r.data).catch(() => null);
      if (!p) { toast.error("Producto no encontrado"); navigate("/productos"); return; }
      setProd(p);
      const [{ data: e }, { data: c }, { data: m }, { data: cmp }, { data: bw }] = await Promise.all([
        api.get(`/products/${id}/inventory-entries`),
        api.get(`/products/${id}/consumptions`),
        api.get(`/products/${id}/kardex`),
        // Solo admin ve costos: para otros roles no debe romper la carga
        api.get(`/products/${id}/cost-comparison`).catch(() => ({ data: null })),
        api.get(`/products/${id}/stock-by-warehouse`),
      ]);
      setEntries(e); setConsumptions(c); setMovs(m); setComparison(cmp); setByWarehouse(bw);
    } catch (err) { toast.error(apiErr(err)); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [id]);

  if (!prod) return <Layout title="Cargando…">…</Layout>;

  return (
    <Layout
      title={
        <span className="flex items-center gap-2">
          <span className="font-mono text-agro-green">{prod.codigo}</span>
          <span className="text-text-secondary"> · </span>
          <span>{prod.sku || prod.categoria}</span>
          {prod.es_semilla && (
            <span title="Producto semilla" className="ml-2 inline-flex items-center gap-1 text-agro-yellow text-xs bg-agro-yellow/10 border border-agro-yellow/30 rounded px-2 py-0.5">
              <FlowerIcon size={12} weight="fill" /> Semilla
            </span>
          )}
        </span>
      }
      subtitle={`${prod.categoria} · ${prod.marca} · ${fmtNum(prod.stock_actual)} ${prod.unidad}`}
      actions={
        <button
          data-testid="back-products-btn"
          onClick={() => navigate("/productos")}
          className="border border-border hover:border-agro-green/40 rounded-md px-3 py-2 text-sm flex items-center gap-2 text-text-secondary hover:text-text-primary"
        >
          <ArrowLeftIcon size={16} /> Volver
        </button>
      }
    >
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="product-detail-kpis">
        <KPI label="Stock actual" value={`${fmtNum(prod.stock_actual)} ${prod.unidad}`} color="text-text-primary" />
        {!sinCostos && <>
        <KPI label="PC PEPS (real)" value={fmtL(comparison?.pc_peps || prod.pc_peps)} color="text-agro-green" testid="kpi-pc-peps" />
        <KPI label="PC Ponderado (visual)" value={fmtL(comparison?.pc_ponderado || prod.pc_ponderado)} color="text-agro-yellow" testid="kpi-pc-pond" />
        <KPI label="Último costo ingreso" value={fmtL(prod.ultimo_costo)} color="text-text-secondary" />
        </>}
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-4 flex gap-1 overflow-x-auto" data-testid="product-detail-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-all ${
              tab === t.id
                ? "border-agro-green text-agro-green font-medium"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoTab prod={prod} />}
      {tab === "peps" && <PepsTab entries={entries} consumptions={consumptions} esSemilla={prod.es_semilla} onChanged={load} />}
      {tab === "bodegas" && <BodegasTab rows={byWarehouse} />}
      {tab === "movs" && <MovsTab movs={movs} sinCostos={sinCostos} />}
      {tab === "precios" && <PreciosTab prod={prod} sinCostos={sinCostos} />}
      {tab === "documentos" && <DocumentCenter entityType="product" entityId={prod.id} title="Documentos del producto" />}
    </Layout>
  );
}

function KPI({ label, value, color, testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 font-medium">{label}</div>
      <div className={`text-2xl font-heading font-semibold ${color || "text-text-primary"}`}>{value}</div>
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div className="grid grid-cols-2 py-2 border-b border-border/40 text-sm">
      <span className="text-text-muted">{k}</span>
      <span className="text-text-primary">{v ?? "—"}</span>
    </div>
  );
}

function InfoTab({ prod }) {
  return (
    <div className="space-y-4">
    {(prod.imagen_url || prod.notas) && (
      <div className="bg-card border border-border/60 rounded-lg p-5 flex gap-5 items-start" data-testid="product-media">
        {prod.imagen_url && <img src={prod.imagen_url} alt={prod.nombre || prod.codigo} className="w-40 h-40 object-contain rounded-md border border-border bg-app" />}
        {prod.notas && (
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Notas internas</div>
            <div className="text-sm text-text-secondary whitespace-pre-wrap">{prod.notas}</div>
          </div>
        )}
      </div>
    )}
    <div className="bg-card border border-border/60 rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8">
      <div>
        <InfoRow k="Código" v={prod.codigo} />
        <InfoRow k="SKU" v={prod.sku} />
        <InfoRow k="Categoría" v={prod.categoria} />
        <InfoRow k="Subcategoría" v={prod.subcategoria} />
        <InfoRow k="Marca" v={prod.marca} />
        <InfoRow k="Unidad" v={prod.unidad} />
        <InfoRow k="Bodega" v={prod.ubicacion_bodega} />
        <InfoRow k="Código de barras" v={prod.codigo_barras || "—"} />
        <InfoRow k="Unidad de compra" v={prod.unidad_compra ? `${prod.unidad_compra} (1 = ${prod.factor_compra} ${prod.unidad})` : "Igual a almacén"} />
        <InfoRow k="Unidad de venta" v={prod.unidad_venta ? `${prod.unidad_venta} (1 = ${prod.factor_venta} ${prod.unidad})` : "Igual a almacén"} />
      </div>
      <div>
        <InfoRow k="Stock actual" v={`${fmtNum(prod.stock_actual)} ${prod.unidad}`} />
        <InfoRow k="Stock mínimo" v={fmtNum(prod.stock_minimo)} />
        <InfoRow k="Stock máximo" v={fmtNum(prod.stock_maximo)} />
        <InfoRow k="ISV" v={`${prod.isv}%`} />
        <InfoRow k="Es semilla" v={prod.es_semilla ? "Sí (PEPS por fecha de test)" : "No"} />
        <InfoRow k="Proveedor principal" v={prod.proveedor_principal} />
        <InfoRow k="Vencimiento" v={prod.fecha_vencimiento ? fmtDate(prod.fecha_vencimiento) : "—"} />
        <InfoRow k="Cuenta inventario / costo" v={`${prod.cuenta_contable_codigo || "1104"} / ${prod.cuenta_costo_codigo || "5101"}`} />
        <InfoRow k="Estado" v={prod.activo === false ? "Inactivo" : "Activo"} />
      </div>
    </div>
    </div>
  );
}

function PepsTab({ entries, consumptions, esSemilla, onChanged }) {
  const editUbicacion = async (e) => {
    const v = window.prompt(`Ubicación física del ingreso ${e.numero_ingreso || ""} (pasillo / estante):`, e.ubicacion || "");
    if (v === null) return;
    try { await api.patch(`/inventory/entries/${e.id}`, { ubicacion: v }); toast.success("Ubicación actualizada"); onChanged && onChanged(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const conSaldo = entries.filter((e) => e.cantidad_restante > 0);
  const [verFechaFact, setVerFechaFact] = usePrefFechaFacturacion();
  const [factura, setFactura] = useState(null);
  // Columnas visibles según categoría (INV-02). N° fijas: Fecha ingreso + Batch + Lote fab + Referencia + Cant. inicial + Cant. restante + Precio compra
  const fechaCols = esSemilla ? 1 : 2; // semilla: solo F. test · producto: F. fab + F. vence
  const totalCols = 13 + fechaCols + (verFechaFact ? 1 : 0);
  return (
    <div className="space-y-6">
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60">
          <div className="font-heading font-semibold text-text-primary mb-1">Lotes PEPS — Ingresos del producto</div>
          <div className="text-xs text-text-muted">
            {esSemilla
              ? "Categoría: Semilla · Orden de salida: fecha de test (germinación) más antigua primero."
              : "Categoría: Producto · Orden de salida: fecha de ingreso más antigua primero."}
            {" "}Cada ingreso conserva su propio costo (precio de compra + flete / gastos asignados); las salidas consumen las capas en orden PEPS.
          </div>
          <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
            <input type="checkbox" checked={verFechaFact} onChange={(e) => setVerFechaFact(e.target.checked)} className="accent-agro-green" data-testid="peps-col-fecha-factura" />
            Mostrar fecha de facturación
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">N° ingreso</th>
                <th className="p-3" title="Ingreso físico (orden PEPS)">Fecha ingreso</th>
                {verFechaFact && <th className="p-3">Fecha de facturación</th>}
                <th className="p-3">Proveedor</th>
                <th className="p-3">Bodega / Ubicación</th>
                <th className="p-3">Batch</th>
                <th className="p-3">Lote fabricación</th>
                {esSemilla ? (
                  <th className="p-3">Fecha test</th>
                ) : (
                  <>
                    <th className="p-3">Fecha fab.</th>
                    <th className="p-3">Vence</th>
                  </>
                )}
                <th className="p-3">Referencia</th>
                <th className="p-3">Origen</th>
                <th className="p-3 text-right">Cant. inicial</th>
                <th className="p-3 text-right">Cant. restante</th>
                <th className="p-3 text-right" title="Precio de compra según la factura (neto de descuento, sin ISV)">Precio compra</th>
                <th className="p-3 text-right" title="Flete y gastos de importación asignados a este ingreso (por unidad)">Gastos asig.</th>
                <th className="p-3 text-right pr-5" title="Costo unitario de la capa PEPS">Costo final</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={totalCols} className="p-8 text-center text-text-muted">No hay lotes registrados</td></tr>
              ) : entries.map((e, i) => (
                <tr key={e.id} className={`border-t border-border/40 ${e.cantidad_restante > 0 ? "" : "opacity-50"}`} data-testid={`peps-entry-${i}`}>
                  <td className="p-3 pl-5 text-xs font-mono text-agro-green" data-testid={`peps-ing-${i}`}>{e.numero_ingreso || "—"}</td>
                  <td className="p-3 text-xs">{fmtDate(e.fecha_ingreso)}</td>
                  {verFechaFact && <td className="p-3 text-xs" data-testid={`peps-fecha-factura-${i}`}>{e.fecha_factura ? fmtDate(e.fecha_factura) : "—"}</td>}
                  <td className="p-3 text-xs text-text-secondary">{e.proveedor_nombre || "—"}</td>
                  <td className="p-3 text-xs">
                    <div className="font-mono">{e.bodega_codigo || "—"}</div>
                    <button type="button" onClick={() => editUbicacion(e)} className="text-[10px] text-text-muted hover:text-agro-yellow" title="Editar ubicación">
                      {e.ubicacion || "+ ubicación"}
                    </button>
                  </td>
                  <td className="p-3 text-xs font-mono">{e.lote || "—"}</td>
                  <td className="p-3 text-xs font-mono text-text-secondary">{e.lote_fabricacion || "—"}</td>
                  {esSemilla ? (
                    <td className="p-3 text-xs text-agro-yellow">{e.fecha_test ? fmtDate(e.fecha_test) : "—"}</td>
                  ) : (
                    <>
                      <td className="p-3 text-xs">{e.fecha_fabricacion ? fmtDate(e.fecha_fabricacion) : "—"}</td>
                      <td className="p-3 text-xs">{e.fecha_vencimiento ? fmtDate(e.fecha_vencimiento) : "—"}</td>
                    </>
                  )}
                  <td className="p-3 text-xs text-text-secondary">{e.factura_compra_id
                    ? <button type="button" onClick={() => setFactura(e)} className="text-agro-green hover:underline" title="Ver la factura de compra" data-testid={`peps-factura-${i}`}>{e.referencia}<div className="text-[10px] text-text-muted">Fact. {e.num_factura_proveedor}</div></button>
                    : e.referencia}</td>
                  <td className="p-3 text-xs text-text-muted">{e.origen}</td>
                  <td className="p-3 text-right">{fmtNum(e.cantidad_inicial)}</td>
                  <td className="p-3 text-right font-medium text-agro-green">{fmtNum(e.cantidad_restante)}</td>
                  <td className="p-3 text-right text-text-secondary">{e.precio_compra_unitario !== undefined && e.precio_compra_unitario !== null ? fmtL(e.precio_compra_unitario) : fmtL(e.costo_unitario)}</td>
                  <td className="p-3 text-right text-agro-yellow">{e.gasto_asignado_unitario ? fmtL(e.gasto_asignado_unitario) : "—"}</td>
                  <td className="p-3 pr-5 text-right font-semibold text-text-primary" data-testid={`peps-costo-${i}`}>{fmtL(e.costo_unitario)}</td>
                </tr>
              ))}
            </tbody>
            {conSaldo.length > 0 && (
              <tfoot className="bg-sidebar">
                <tr className="text-xs">
                  <td className="p-3 pl-5 font-semibold" colSpan={totalCols - 5}>Total con saldo</td>
                  <td className="p-3 text-right"></td>
                  <td className="p-3 text-right font-semibold text-agro-green">
                    {fmtNum(conSaldo.reduce((s, e) => s + (e.cantidad_restante || 0), 0))}
                  </td>
                  <td className="p-3 pr-5" colSpan="3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {factura && (
          <DocumentoPdfModal url={`/purchase-invoices/${factura.factura_compra_id}/pdf`} titulo="Factura de compra"
            numero={`${factura.factura_compra_numero || factura.referencia} · proveedor ${factura.num_factura_proveedor || ""}`}
            archivo={`factura-compra-${factura.factura_compra_numero || factura.referencia}.pdf`} onClose={() => setFactura(null)} testid="peps-factura-pdf" />
        )}
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 font-heading font-semibold">Consumos PEPS — Salidas</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Fecha</th>
                <th className="p-3">Referencia</th>
                <th className="p-3">Tipo</th>
                <th className="p-3 text-right">Cantidad</th>
                <th className="p-3 text-right">Costo unit.</th>
                <th className="p-3 text-right pr-5">Costo total</th>
              </tr>
            </thead>
            <tbody>
              {consumptions.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin consumos</td></tr>
              ) : consumptions.map((c) => (
                <tr key={c.id} className={`border-t border-border/40 ${c.revertido ? "opacity-40 line-through" : ""}`}>
                  <td className="p-3 pl-5 text-xs">{fmtDate(c.fecha)}</td>
                  <td className="p-3 text-xs text-text-secondary">{c.referencia}</td>
                  <td className="p-3 text-xs">
                    <span className="px-2 py-0.5 rounded bg-app border border-border text-text-secondary">{c.tipo}</span>
                  </td>
                  <td className="p-3 text-right">{fmtNum(c.cantidad)}</td>
                  <td className="p-3 text-right">{fmtL(c.costo_unitario)}</td>
                  <td className="p-3 pr-5 text-right font-medium text-agro-green">{fmtL(c.costo_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BodegasTab({ rows }) {
  const total = rows.reduce((s, r) => s + (r.cantidad || 0), 0);
  const valor = rows.reduce((s, r) => s + (r.valor_total || 0), 0);
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <div>
          <div className="font-heading font-semibold text-text-primary">Stock distribuido por bodega</div>
          <div className="text-xs text-text-muted">Cada lote PEPS vive en una bodega específica. La suma es el stock total del producto.</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Total general</div>
          <div className="text-lg font-heading font-semibold text-agro-green">{fmtNum(total)}</div>
          <div className="text-[11px] text-text-muted">{fmtL(valor)}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Bodega</th>
              <th className="p-3">Ubicación</th>
              <th className="p-3">Tipo</th>
              <th className="p-3 text-right">Lotes</th>
              <th className="p-3 text-right">Cantidad</th>
              <th className="p-3 text-right pr-5">Valor</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin stock en ninguna bodega</td></tr>
            ) : rows.map((r) => (
              <tr key={r.bodega_id || r.bodega_codigo} className="border-t border-border/40" data-testid={`stock-bodega-${r.bodega_codigo}`}>
                <td className="p-3 pl-5">
                  <div className="font-mono text-xs text-agro-green">{r.bodega_codigo}</div>
                  <div className="text-[11px] text-text-muted">{r.bodega_nombre}</div>
                </td>
                <td className="p-3 text-xs text-text-secondary">{r.ubicacion || "—"}</td>
                <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded bg-app border border-border text-text-secondary capitalize">{r.tipo || "—"}</span></td>
                <td className="p-3 text-right text-xs">{r.lotes}</td>
                <td className="p-3 text-right font-medium">{fmtNum(r.cantidad)}</td>
                <td className="p-3 pr-5 text-right font-medium text-text-primary">{fmtL(r.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const MOV_LABEL = {
  entrada_devolucion_venta: "Devolución de venta",
  devolucion_no_vendible: "Devolución no vendible",
  reverso_devolucion_venta: "Reverso devolución",
};
const DESTINO_LABEL = { dano: "baja por daño", vencimiento: "baja por vencimiento", merma: "merma" };

function MovsTab({ movs, sinCostos }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Fecha</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Referencia</th>
              <th className="p-3 text-right">Cantidad</th>
              {!sinCostos && <th className="p-3 text-right pr-5">Costo</th>}
            </tr>
          </thead>
          <tbody>
            {movs.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-text-muted">No hay movimientos</td></tr>
            ) : movs.map((m) => (
              <tr key={m.id} className="border-t border-border/40">
                <td className="p-3 pl-5 text-xs">{fmtDate(m.fecha)}</td>
                <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded bg-app border border-border">{MOV_LABEL[m.tipo] || m.tipo}</span>
                  {m.destino && <div className="text-[10px] text-agro-yellow mt-0.5">No suma al stock · {DESTINO_LABEL[m.destino] || m.destino}</div>}</td>
                <td className="p-3 text-xs text-text-secondary">{m.referencia}
                  {m.factura_numero && <span className="text-text-muted"> · Fact. {m.factura_numero}</span>}
                  {(m.bodega_codigo || m.lote) && <div className="text-[10px] text-text-muted">{[m.bodega_codigo, m.lote && `Lote ${m.lote}`].filter(Boolean).join(" · ")}</div>}</td>
                <td className={`p-3 text-right font-medium ${m.cantidad > 0 ? "text-agro-green" : "text-red-400"}`}>{m.cantidad > 0 ? "+" : ""}{fmtNum(m.cantidad)}</td>
                {!sinCostos && <td className="p-3 pr-5 text-right">{m.costo_unitario ? fmtL(m.costo_unitario) : "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriceCell({ label, value, special, locked, fijadoEn }) {
  return (
    <div className={`bg-card border ${special ? "border-agro-yellow/40" : "border-border/60"} rounded-lg p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 flex items-center gap-2">
        {label}
        {special && <span className="text-agro-yellow normal-case text-[10px]">se fija al cerrar</span>}
        {locked && <span className="text-agro-green text-[10px] normal-case">fijado {fijadoEn}</span>}
      </div>
      <div className={`text-2xl font-heading font-semibold ${special ? "text-agro-yellow" : "text-text-primary"}`}>{fmtL(value)}</div>
    </div>
  );
}

function PreciosTab({ prod, sinCostos }) {
  return (
    <div className="space-y-4">
      <div className="bg-card border border-agro-yellow/20 rounded-lg p-4 text-xs text-text-secondary leading-relaxed">
        <strong className="text-agro-yellow">Doble lectura del costo:</strong> el PC PEPS muestra el costo real del ingreso que sale (rentabilidad real),
        mientras que el PC ponderado se calcula como <code className="text-agro-green">Σ(costo × existencia) ÷ Σ existencias</code> y se usa solo para visualización.
        El precio P0 es el “precio especial” que se fija al cerrar el período (mensual) — todos los demás precios (P1-P4) son los niveles habituales de venta.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <PriceCell label="P0 (especial)" value={prod.precio_p0} special locked={!!prod.p0_fijado_en} fijadoEn={prod.p0_fijado_en} />
        <PriceCell label="P1" value={prod.precio_p1} />
        <PriceCell label="P2" value={prod.precio_p2} />
        <PriceCell label="P3" value={prod.precio_p3} />
        <PriceCell label="P4" value={prod.precio_p4} />
      </div>
      {!sinCostos && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
        <div className="bg-card border border-border/60 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">PC PEPS (real)</div>
          <div className="text-xl font-heading font-semibold text-agro-green">{fmtL(prod.pc_peps)}</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">PC ponderado (visual)</div>
          <div className="text-xl font-heading font-semibold text-text-secondary">{fmtL(prod.pc_ponderado)}</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Margen vs P1 (PEPS)</div>
          <div className="text-xl font-heading font-semibold text-agro-yellow">
            {prod.precio_p1 && prod.pc_peps
              ? `${(((prod.precio_p1 - prod.pc_peps) / prod.precio_p1) * 100).toFixed(1)}%`
              : "—"}
          </div>
        </div>
      </div>}
    </div>
  );
}
